import { useState, type InputHTMLAttributes, type SelectHTMLAttributes } from 'react'

interface FormFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string
  error?: string
  hint?: string
}

function EyeIcon({ open }: { open: boolean }) {
  return open ? (
    <svg width='18' height='18' viewBox='0 0 24 24' fill='none'>
      <path d='M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
      <circle cx='12' cy='12' r='3' stroke='currentColor' strokeWidth='2'/>
    </svg>
  ) : (
    <svg width='18' height='18' viewBox='0 0 24 24' fill='none'>
      <path d='M17.94 17.94A10.94 10.94 0 0112 19c-7 0-11-7-11-7a21.3 21.3 0 015.06-5.94M9.9 4.24A10.4 10.4 0 0112 4c7 0 11 7 11 7a21.3 21.3 0 01-2.73 3.88M14.12 14.12a3 3 0 11-4.24-4.24' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
      <path d='M1 1l22 22' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'/>
    </svg>
  )
}

export function FormField({ label, error, hint, type, ...inputProps }: FormFieldProps) {
  const [visible, setVisible] = useState(false)
  const isPassword = type === 'password'

  return (
    <div>
      {label && <label className='field-label'>{label}</label>}
      <div style={{ position: 'relative' }}>
        <input
          {...inputProps}
          type={isPassword ? (visible ? 'text' : 'password') : type}
          className={`field-input${error ? ' error' : ''}`}
          style={isPassword ? { paddingRight: 40, ...inputProps.style } : inputProps.style}
        />
        {isPassword && (
          <button
            type='button'
            onClick={() => setVisible(v => !v)}
            aria-label={visible ? 'Hide password' : 'Show password'}
            tabIndex={-1}
            style={{
              position: 'absolute', top: '50%', right: 12, transform: 'translateY(-50%)',
              background: 'none', border: 'none', padding: 0, margin: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'rgba(27,42,74,0.4)', cursor: 'pointer',
            }}
          >
            <EyeIcon open={visible} />
          </button>
        )}
      </div>
      {error && (
        <div style={{ font: '400 11.5px Inter,sans-serif', color: '#E0663B', marginTop: 5 }}>
          {error}
        </div>
      )}
      {!error && hint && (
        <div style={{ font: '400 11.5px Inter,sans-serif', color: 'rgba(27,42,74,0.45)', marginTop: 5 }}>
          {hint}
        </div>
      )}
    </div>
  )
}

interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string
  error?: string
  hint?: string
  children: React.ReactNode
}

export function SelectField({ label, error, hint, children, ...selectProps }: SelectFieldProps) {
  return (
    <div>
      <label className='field-label'>{label}</label>
      <select
        {...selectProps}
        className={`field-input${error ? ' error' : ''}`}
        style={{ cursor: 'pointer', ...(selectProps.style ?? {}) }}
      >
        {children}
      </select>
      {error && (
        <div style={{ font: '400 11.5px Inter,sans-serif', color: '#E0663B', marginTop: 5 }}>
          {error}
        </div>
      )}
      {!error && hint && (
        <div style={{ font: '400 11.5px Inter,sans-serif', color: 'rgba(27,42,74,0.45)', marginTop: 5 }}>
          {hint}
        </div>
      )}
    </div>
  )
}
